// The overview's "Staked" group under Your holdings (docs/19-staking-plan.md
// 6.2, 4.8, 6.4): one row per stake account the vault controls alone, with
// its status (Activating, Earning, Unstaking, Ready to move, from the
// account's state and the network's stake history through the SDK), its
// validator's name and current commission (and the commission at delegation
// when this device recorded it), the amount, "Earned so far" from the
// account's own transactions, and the actions its status allows, each a link
// to the Stake page's review. Accounts that name the vault without being
// its alone are listed read-only. Everything comes from the vault's address
// alone, so a restored device shows the same rows with nothing backed up.
// Reading only: nothing here signs. A vault with no stake shows nothing.

import { useEffect, useState } from 'react';
import { Link } from 'wouter';
import type { Engine, StakeHoldings } from '../engine';
import { describe } from '../engine';
import { type PositionView, type VoteCheck, bpsText, commissionAtDelegation, earnedSoFar, readVote, validatorName } from '../staking';
import { Amount, StatusLine } from './primitives';

const TWO_SOL = 2_000_000_000n;
const ONE_SOL = 1_000_000_000n;

interface Extra {
  earned: Map<string, bigint | null>;
  votes: Map<string, VoteCheck>;
}

function tagClass(status: PositionView['status']): string {
  return status === 'Earning' ? 'accent' : status === 'Ready to move' ? '' : 'pending';
}

export function StakedGroup({ engine, rpcUrl, version }: { engine: Engine; rpcUrl: string; version: number | null }) {
  const [holdings, setHoldings] = useState<StakeHoldings | null>(null);
  const [extra, setExtra] = useState<Extra>({ earned: new Map(), votes: new Map() });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void engine
      .stakePositions()
      .then(async (h) => {
        if (cancelled) return;
        setHoldings(h);
        setError(null);
        const all = [...h.owned, ...h.named];
        if (!all.length) return;
        const votes = new Map<string, VoteCheck>();
        for (const v of new Set(all.map((p) => p.vote).filter((x): x is string => !!x))) votes.set(v, await readVote(engine.rpc, v, h.epoch));
        const earned = new Map<string, bigint | null>();
        for (const p of h.owned) earned.set(p.address, await earnedSoFar(engine.rpc, p.address, p.lamports));
        if (!cancelled) setExtra({ earned, votes });
      })
      .catch((e) => {
        if (!cancelled) setError(describe(e));
      });
    return () => {
      cancelled = true;
    };
  }, [engine, version]);

  if (error) {
    return (
      <StatusLine state="neutral" word="Staking not listed.">
        Your stake accounts could not be read: {error}
      </StatusLine>
    );
  }
  if (!holdings || (!holdings.owned.length && !holdings.named.length)) return null;
  const acts = !!holdings.gate?.on;

  const validatorCell = (p: PositionView) => {
    if (!p.vote) return null;
    const check = extra.votes.get(p.vote);
    const at = p.status !== 'Ready to move' ? commissionAtDelegation(p.address, p.vote) : null;
    const now = check?.info?.commissionBps;
    return (
      <small>
        {validatorName(rpcUrl, p.vote) ?? `Validator ${p.vote.slice(0, 4)}…${p.vote.slice(-4)}`}
        {now !== undefined ? `, commission ${bpsText(now)}` : ''}
        {at !== null && now !== undefined && now > at ? ` (up from ${bpsText(at)} when you staked)` : at !== null ? ` (${bpsText(at)} when you staked)` : ''}
        {check?.warnings.length ? `. ${check.warnings[0]}` : ''}
      </small>
    );
  };

  const actions = (p: PositionView) => {
    if (!acts || p.n === null) return null;
    const href = (action: string) => `/stake?action=${action}&n=${p.n}`;
    const links: { action: string; label: string; danger?: boolean }[] = [];
    if (p.status === 'Activating' || p.status === 'Earning') {
      links.push({ action: 'unstake', label: 'Unstake' });
      if (p.delegated >= TWO_SOL) links.push({ action: 'part', label: 'Unstake part' });
    }
    if (p.extra > 0n) links.push({ action: 'collect', label: 'Collect extra' });
    if (p.status === 'Ready to move') {
      if (p.withdrawable > 0n) links.push({ action: 'savings', label: 'Move to savings' });
      links.push({ action: 'again', label: 'Stake again' });
      if (p.vote && p.withdrawable >= ONE_SOL) links.push({ action: 'switch', label: 'Switch validator' });
      if (p.closable) links.push({ action: 'close', label: 'Close', danger: true });
    }
    return (
      <div className="actions">
        {links.map((l) => (
          <Link key={l.action} href={href(l.action)} className={`button small ${l.danger ? 'danger' : 'ghost'}`}>
            {l.label}
          </Link>
        ))}
      </div>
    );
  };

  return (
    <div className="staked" aria-labelledby="staked-title">
      <div className="console-heading">
        <h4 id="staked-title">Staked</h4>
        <span className="dim">{`${String(holdings.owned.length).padStart(2, '0')} ${holdings.owned.length === 1 ? 'POSITION' : 'POSITIONS'}`}</span>
      </div>
      {holdings.owned.length ? (
        <table className="table assets staked-positions">
          <thead className="sr-only">
            <tr>
              <th>Position</th>
              <th>In it</th>
            </tr>
          </thead>
          <tbody>
            {holdings.owned.map((p) => {
              const earned = extra.earned.get(p.address);
              return (
                <tr key={p.address} data-stake-account={p.address}>
                  <td>
                    <span className="asset-id">
                      <span className="token-glyph t-sol" aria-hidden="true">
                        S
                      </span>
                      <span>
                        {p.n !== null ? `Stake account ${p.n}` : `Stake account ${p.address.slice(0, 4)}…`}
                        {validatorCell(p)}
                      </span>
                    </span>
                  </td>
                  <td>
                    <span className="num">
                      <Amount raw={p.lamports} decimals={9} symbol="SOL" maxFraction={9} />
                    </span>
                    <span className={`tag ${tagClass(p.status)}`.trim()}>{p.status}</span>
                    <small className="avail">
                      {p.delegated > 0n && p.status !== 'Ready to move' ? (
                        <>
                          <Amount raw={p.delegated} decimals={9} symbol="SOL" /> staked.{' '}
                        </>
                      ) : null}
                      Earned so far: {earned === undefined ? 'reading…' : earned === null ? 'unavailable' : <Amount raw={earned} decimals={9} symbol="SOL" maxFraction={9} />}
                    </small>
                    {actions(p)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : null}
      {holdings.named.length ? (
        <div className="stack tight">
          <p className="ink">These stake accounts name your vault but are not controlled by it alone (a gift, or one with a lockup). They are listed only; the app offers no action on them.</p>
          <ul className="named-stake">
            {holdings.named.map((p) => (
              <li key={p.address}>
                <span className="mono">{p.address}</span> <Amount raw={p.lamports} decimals={9} symbol="SOL" />
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {holdings.owned.length && !acts && holdings.gate?.reason ? <p className="activity-note">{holdings.gate.reason}</p> : null}
      <p className="activity-note">Found from your vault&rsquo;s address alone; nothing else needs backing up. &ldquo;Earned so far&rdquo; is the balance minus what the account&rsquo;s own transactions moved in and out, read from the network: an estimate, not a promise.</p>
    </div>
  );
}

/** Restore's key refresh step (docs/19 6.4): the stake accounts found from the vault's address alone, so the user sees nothing needed backing up. Nothing when none are found or the read fails. */
export function RestoredStakeNote({ engine }: { engine: Engine }) {
  const [found, setFound] = useState<{ count: number; lamports: bigint } | null>(null);
  useEffect(() => {
    let cancelled = false;
    void engine
      .stakePositions()
      .then((h) => {
        const all = [...h.owned, ...h.named];
        if (!cancelled && all.length) setFound({ count: all.length, lamports: all.reduce((s, p) => s + p.lamports, 0n) });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [engine]);
  if (!found) return null;
  return (
    <p className="b restored-stake">
      Staked: {found.count} {found.count === 1 ? 'stake account' : 'stake accounts'} with <Amount raw={found.lamports} decimals={9} symbol="SOL" maxFraction={9} />, found from your vault&rsquo;s address alone. Nothing else needed backing up; they show under Your holdings once the key refresh lands.
    </p>
  );
}
